import React, { useMemo, useState } from "react";
import { ArrowLeft, Loader2, Mail, MessageSquare, PencilLine, Phone, RefreshCw } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { useAdminFetch } from "../admin/hooks/useAdminFetch";
import {
  STAKEHOLDER_CONTACT_ACTIONS,
  STAKEHOLDER_FOLLOW_UP_TYPES,
  STAKEHOLDER_URGENCY_OVERRIDE_OPTIONS,
} from "./config/stakeholderContactActions";
import {
  STAKEHOLDER_DISPOSITION_REASON_OPTIONS,
  STAKEHOLDER_GUIDE_TARGET_OPTIONS,
  STAKEHOLDER_MEMBER_TARGET_OPTIONS,
} from "./config/stakeholderStatusOptions";
import { getStakeholderLaneAppearance, LANE_STATUS_META } from "./config/stakeholderLaneConfig";
import { useStakeholderContactHistory } from "./hooks/useStakeholderContactHistory";
import { useStakeholderDispositionHistory } from "./hooks/useStakeholderDispositionHistory";

const formatDateTime = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatDispositionState = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();

const getInitials = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("") || "?";

function ContextBlock({ label, value }) {
  if (!value) return null;
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white/90 p-4 dark:border-white/10 dark:bg-white/[0.04]">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">{label}</p>
      <p className="mt-2 text-sm leading-6 text-slate-700 dark:text-slate-200">{value}</p>
    </div>
  );
}

const getMemberCurrentState = (item) => {
  if (!item) return "";
  if ((item.status || "").toLowerCase() === "former") return "former";
  return item.lane === "dad-partners" ? "dad_partner" : "prospect";
};

const getGuideCurrentState = (item) => {
  if (!item) return "";
  const inviteStage = (item.invite_stage || item.invite_stage_label || "").toLowerCase();
  const status = (item.guide_status || item.guide_status_label || "").toLowerCase();
  if (inviteStage.includes("declined")) return "declined";
  if (inviteStage.includes("scheduled") || status.includes("scheduled")) return "scheduled";
  if (inviteStage.includes("completed") || status.includes("completed")) return "completed";
  if (status.includes("active")) return "active";
  if (inviteStage && !inviteStage.includes("not invited")) return "invited";
  return "prospect";
};

const getTargetStateForItem = (item) => {
  if (!item) return "";
  if (item.source_type === "member") return getMemberCurrentState(item);
  if (item.source_type === "guide") return getGuideCurrentState(item);
  return "";
};

const getSourceStatusOptions = (item) => {
  if (!item) return [];
  if (item.source_type === "member") return STAKEHOLDER_MEMBER_TARGET_OPTIONS;
  if (item.source_type === "guide") return STAKEHOLDER_GUIDE_TARGET_OPTIONS;
  return [];
};

const requiresDispositionReason = (item, targetState) =>
  !!item &&
  (
    (item.source_type === "member" && targetState === "former") ||
    (item.source_type === "guide" && targetState === "declined")
  );

const buildPersonRouteForTransition = (item, targetState) => {
  if (!item?.source_id) return "/community";
  if (item.source_type === "member") {
    if (targetState === "prospect") return `/community/person/prospect-${encodeURIComponent(item.source_id)}`;
    if (targetState === "dad_partner") return `/community/person/dad-${encodeURIComponent(item.source_id)}`;
    return "/community";
  }
  if (item.source_type === "guide") {
    if (targetState === "declined") return "/community";
    return `/community/person/guide-${encodeURIComponent(item.source_id)}`;
  }
  return "/community";
};

export default function StakeholderPersonPage({ fetchRemoteState, items, setSelectedItemId, updateItem }) {
  const adminFetch = useAdminFetch();
  const navigate = useNavigate();
  const location = useLocation();
  const { itemId } = useParams();
  const contactTimelineRef = React.useRef(null);
  const [editingEntryId, setEditingEntryId] = useState("");
  const [editingEntryAction, setEditingEntryAction] = useState("");
  const [editingEntryNote, setEditingEntryNote] = useState("");
  const [noteDraft, setNoteDraft] = useState("");
  const [nextMoveDraft, setNextMoveDraft] = useState("");
  const [followUpDateDraft, setFollowUpDateDraft] = useState("");
  const [followUpTypeDraft, setFollowUpTypeDraft] = useState("");
  const [followUpNoteDraft, setFollowUpNoteDraft] = useState("");
  const [urgencyOverrideDraft, setUrgencyOverrideDraft] = useState("auto");
  const [targetStateDraft, setTargetStateDraft] = useState("");
  const [dispositionReasonDraft, setDispositionReasonDraft] = useState("");
  const [dispositionNoteDraft, setDispositionNoteDraft] = useState("");
  const [savingStatusChange, setSavingStatusChange] = useState(false);

  const item = useMemo(
    () => items.find((entry) => entry.id === itemId) || null,
    [items, itemId]
  );

  const {
    contactState,
    deleteAction,
    deletingEntryId,
    editAction,
    entries,
    error,
    loading,
    logAction,
    refresh,
    savingAction,
    savingEntryId,
    savingFollowUp,
    saveFollowUp,
  } = useStakeholderContactHistory(item);
  const {
    entries: dispositionEntries,
    error: dispositionError,
    loading: dispositionLoading,
  } = useStakeholderDispositionHistory(item);

  React.useEffect(() => {
    setNextMoveDraft(item?.next_move || "");
  }, [item?.next_move, item?.id]);

  React.useEffect(() => {
    setFollowUpDateDraft(contactState?.follow_up_date || item?.follow_up_date || "");
    setFollowUpTypeDraft(contactState?.follow_up_type || item?.follow_up_type || "");
    setFollowUpNoteDraft(contactState?.follow_up_note || item?.follow_up_note || "");
    setUrgencyOverrideDraft(contactState?.urgency_override || item?.urgency_override || "auto");
  }, [
    contactState?.follow_up_date,
    contactState?.follow_up_note,
    contactState?.follow_up_type,
    contactState?.urgency_override,
    item?.follow_up_date,
    item?.follow_up_note,
    item?.follow_up_type,
    item?.urgency_override,
    item?.id,
  ]);

  React.useEffect(() => {
    setTargetStateDraft(getTargetStateForItem(item));
    setDispositionReasonDraft("");
    setDispositionNoteDraft("");
  }, [item]);

  React.useEffect(() => {
    if (location.hash !== "#contact-timeline" || !item || !contactTimelineRef.current) return;
    const frame = window.requestAnimationFrame(() => {
      contactTimelineRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [location.hash, item, loading]);

  if (!item) {
    return (
      <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(34,211,238,0.12),_transparent_24%),linear-gradient(180deg,#f8fafc_0%,#eef2f7_100%)] pb-10 dark:bg-[radial-gradient(circle_at_top_left,_rgba(34,211,238,0.09),_transparent_24%),linear-gradient(180deg,#05080d_0%,#0a1118_100%)]">
        <div className="mx-auto max-w-[1200px] px-4 py-6 sm:px-6 lg:px-8">
          <div className="rounded-[30px] border border-slate-200/80 bg-white/85 p-6 shadow-[0_35px_80px_-38px_rgba(15,23,42,0.45)] backdrop-blur dark:border-white/10 dark:bg-[#081018]/90">
            <p className="text-sm text-slate-600 dark:text-slate-300">That community record could not be found.</p>
            <Button className="mt-4" onClick={() => navigate("/community")} variant="outline">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Community Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const laneStatus = LANE_STATUS_META[item.bucket === "needs-now" ? "needs-action" : item.bucket === "watch" ? "watch" : "healthy"];
  const appearance = getStakeholderLaneAppearance(item.lane);

  const applySavedContactState = (savedState) => {
    if (!savedState) return;
    updateItem(item.id, {
      latest_contact_action: savedState.latest_action_type ?? "",
      latest_contact_label: savedState.latest_action_label ?? "",
      latest_contact_note: savedState.latest_note ?? "",
      latest_contact_value: savedState.latest_contact_value ?? "",
      latest_contact_at: savedState.latest_created_at ?? "",
      follow_up_date: savedState.follow_up_date ?? "",
      follow_up_type: savedState.follow_up_type ?? "",
      follow_up_type_label: savedState.follow_up_type_label ?? "",
      follow_up_note: savedState.follow_up_note ?? "",
      urgency_override: savedState.urgency_override ?? "",
      urgency_override_label: savedState.urgency_override_label ?? "",
    });
  };

  const handleLogAction = async (actionType) => {
    const savedState = await logAction({
      actionType,
      note: noteDraft,
    });
    if (savedState) {
      applySavedContactState(savedState);
      setNoteDraft("");
    }
  };

  const handleSaveNextMove = () => {
    updateItem(item.id, { next_move: nextMoveDraft });
  };

  const handleSaveFollowUp = async () => {
    const savedState = await saveFollowUp({
      followUpDate: followUpDateDraft,
      followUpType: followUpTypeDraft,
      followUpNote: followUpNoteDraft,
      urgencyOverride: urgencyOverrideDraft === "auto" ? "" : urgencyOverrideDraft,
    });
    if (savedState) {
      applySavedContactState(savedState);
    }
  };

  const handleClearFollowUp = async () => {
    const savedState = await saveFollowUp({
      followUpDate: "",
      followUpType: "",
      followUpNote: "",
      urgencyOverride: urgencyOverrideDraft === "auto" ? "" : urgencyOverrideDraft,
    });
    if (savedState) {
      setFollowUpDateDraft("");
      setFollowUpTypeDraft("");
      setFollowUpNoteDraft("");
      applySavedContactState(savedState);
    }
  };

  const handleResetUrgency = async () => {
    const savedState = await saveFollowUp({
      followUpDate: followUpDateDraft,
      followUpType: followUpTypeDraft,
      followUpNote: followUpNoteDraft,
      urgencyOverride: "",
    });
    if (savedState) {
      setUrgencyOverrideDraft("auto");
      applySavedContactState(savedState);
    }
  };

  const handleStartEditEntry = (entry) => {
    setEditingEntryId(entry.entry_id || "");
    setEditingEntryAction(entry.action_type || "");
    setEditingEntryNote(entry.note || "");
  };

  const handleCancelEditEntry = () => {
    setEditingEntryId("");
    setEditingEntryAction("");
    setEditingEntryNote("");
  };

  const handleSaveEntryEdit = async () => {
    if (!editingEntryId || !editingEntryAction) return;
    const payload = await editAction({
      entryId: editingEntryId,
      actionType: editingEntryAction,
      note: editingEntryNote,
    });
    if (payload?.state) {
      applySavedContactState(payload.state);
      handleCancelEditEntry();
    }
  };

  const handleDeleteEntry = async (entry) => {
    if (!entry?.entry_id) return;
    const confirmed = window.confirm("Delete this contact log entry?");
    if (!confirmed) return;

    const payload = await deleteAction({
      entryId: entry.entry_id,
    });
    if (payload?.state) {
      applySavedContactState(payload.state);
      if (editingEntryId === entry.entry_id) {
        handleCancelEditEntry();
      }
      toast.success("Contact log entry deleted");
    }
  };

  const handleApplyStatusChange = async () => {
    if (!item?.source_type || !item?.source_id || !targetStateDraft) return;
    if (requiresDispositionReason(item, targetStateDraft) && !dispositionReasonDraft && !dispositionNoteDraft.trim()) {
      toast.error("Removal reason is required");
      return;
    }

    setSavingStatusChange(true);
    try {
      const response = await adminFetch("/admin/stakeholder/dispositions/transition", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          source_type: item.source_type,
          source_id: item.source_id,
          lane: item.lane,
          title_at_time: item.title,
          target_state: targetStateDraft,
          reason_code: dispositionReasonDraft,
          note: dispositionNoteDraft,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.detail || "Failed to update source status");
      }

      await fetchRemoteState();
      const destination = buildPersonRouteForTransition(item, targetStateDraft);
      toast.success("Stakeholder status updated");
      navigate(destination);
    } catch (statusError) {
      toast.error(statusError?.message || "Failed to update source status");
    } finally {
      setSavingStatusChange(false);
    }
  };

  const sourceStatusOptions = getSourceStatusOptions(item);
  const currentSourceState = getTargetStateForItem(item);
  const canManageSourceStatus = sourceStatusOptions.length > 0;

  return (
    <div className={`min-h-screen pb-10 ${appearance.pageClass}`}>
      <div className="mx-auto max-w-[1500px] px-4 py-6 sm:px-6 lg:px-8">
        <div className={`rounded-[30px] border bg-white/85 p-6 shadow-[0_35px_80px_-38px_rgba(15,23,42,0.45)] backdrop-blur dark:bg-[#081018]/90 ${appearance.shellClass}`}>
          <div className="flex flex-col gap-5 border-b border-slate-200/80 pb-6 dark:border-white/10 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex max-w-4xl gap-4">
              <div className="shrink-0">
                {item.photo_url ? (
                  <img
                    alt={item.title}
                    className="h-20 w-20 rounded-2xl border border-slate-200/80 object-cover shadow-sm dark:border-white/10"
                    src={item.photo_url}
                  />
                ) : (
                  <div className="flex h-20 w-20 items-center justify-center rounded-2xl border border-slate-200/80 bg-slate-100 text-xl font-semibold text-slate-700 shadow-sm dark:border-white/10 dark:bg-white/[0.06] dark:text-slate-100">
                    {getInitials(item.title)}
                  </div>
                )}
              </div>

              <div>
                <p className={`text-xs font-semibold uppercase tracking-[0.24em] ${appearance.headingTextClass}`}>
                  Stakeholder Person
                </p>
                <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950 dark:text-white">
                  {item.title}
                </h1>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className="capitalize">
                    {item.lane.replace(/-/g, " ")}
                  </Badge>
                  <Badge className={`border ${laneStatus.className}`}>{laneStatus.label}</Badge>
                  {item.is_kevin_flagged ? <Badge>{item.urgency_label || "Direct"}</Badge> : null}
                  {item.follow_up_status_label ? <Badge variant="outline">{item.follow_up_status_label}</Badge> : null}
                  {item.follow_up_date_label ? <Badge variant="outline">Follow up {item.follow_up_date_label}</Badge> : null}
                </div>
                <p className="mt-4 text-sm leading-6 text-slate-600 dark:text-slate-300">{item.reason}</p>
                {item.what_this_person_needs_now ? (
                  <div className="mt-4 rounded-2xl border border-rose-200/80 bg-rose-50/80 px-4 py-3 text-sm text-rose-900 dark:border-rose-400/20 dark:bg-rose-500/10 dark:text-rose-100">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em]">What This Person Needs Now</p>
                    <p className="mt-2 leading-6">{item.what_this_person_needs_now}</p>
                  </div>
                ) : null}
                {item.contact_label ? (
                  <p className="mt-3 text-sm font-medium text-slate-800 dark:text-slate-100">{item.contact_label}</p>
                ) : null}
                <div className="mt-3 flex flex-wrap gap-2">
                  {item.contact_phone ? (
                    <>
                      <Button asChild size="sm" variant="outline">
                        <a href={`tel:${item.contact_phone}`}>
                          <Phone className="mr-2 h-4 w-4" />
                          Call
                        </a>
                      </Button>
                      <Button asChild size="sm" variant="outline">
                        <a href={`sms:${item.contact_phone}`}>
                          <MessageSquare className="mr-2 h-4 w-4" />
                          Text
                        </a>
                      </Button>
                    </>
                  ) : null}
                  {item.contact_email ? (
                    <Button asChild size="sm" variant="outline">
                      <a href={`mailto:${item.contact_email}`}>
                        <Mail className="mr-2 h-4 w-4" />
                        Email
                      </a>
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={() => navigate(`/community/lane/${item.lane}`)} variant="outline">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to lane
              </Button>
              <Button onClick={() => setSelectedItemId(item.id)} variant="outline">
                <PencilLine className="mr-2 h-4 w-4" />
                Quick edit
              </Button>
            </div>
          </div>

          <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,0.92fr)_minmax(360px,0.48fr)]">
            <div className="space-y-6">
              <section
                className={`rounded-[28px] border p-5 ${appearance.panelClass}`}
                id="contact-timeline"
                ref={contactTimelineRef}
              >
                <div className="flex items-center justify-between gap-3 border-b border-slate-200/80 pb-4 dark:border-white/10">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-950 dark:text-white">Contact timeline</h2>
                    <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                      Shared outreach history for this community record across devices.
                    </p>
                  </div>
                  <Button onClick={refresh} size="sm" variant="outline">
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Refresh
                  </Button>
                </div>

                <div className={`mt-4 rounded-2xl border bg-white/90 p-4 dark:bg-[#0b1118] ${appearance.statClass}`}>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                    Log contact outcome
                  </p>
                  {item.latest_contact_summary ? (
                    <p className="mt-2 text-sm text-slate-700 dark:text-slate-200">{item.latest_contact_summary}</p>
                  ) : null}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {STAKEHOLDER_CONTACT_ACTIONS.map((action) => (
                      <Button
                        disabled={!!savingAction}
                        key={action.value}
                        onClick={() => handleLogAction(action.value)}
                        size="sm"
                        variant={savingAction === action.value ? "default" : "outline"}
                      >
                        {savingAction === action.value ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                        {action.label}
                      </Button>
                    ))}
                  </div>
                  <Textarea
                    className="mt-4 min-h-[96px]"
                    onChange={(event) => setNoteDraft(event.target.value)}
                    placeholder="Optional note: called at lunch, wife answered, asked to try again Thursday, etc."
                    value={noteDraft}
                  />
                  {error ? (
                    <p className="mt-3 text-sm text-rose-600 dark:text-rose-300">{error}</p>
                  ) : null}
                </div>

                <div className="mt-4 space-y-3">
                  {loading ? (
                    <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white/85 px-4 py-4 text-sm text-slate-600 dark:border-white/10 dark:bg-white/[0.04] dark:text-slate-300">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading contact history
                    </div>
                  ) : entries.length ? (
                    entries.map((entry) => (
                      <div
                        className="rounded-2xl border border-slate-200/80 bg-white/90 p-4 dark:border-white/10 dark:bg-white/[0.04]"
                        key={entry.entry_id}
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex flex-wrap items-center gap-2">
                            {editingEntryId === entry.entry_id ? (
                              <Select onValueChange={setEditingEntryAction} value={editingEntryAction}>
                                <SelectTrigger className="h-8 w-[180px]">
                                  <SelectValue placeholder="Select outcome" />
                                </SelectTrigger>
                                <SelectContent>
                                  {STAKEHOLDER_CONTACT_ACTIONS.map((action) => (
                                    <SelectItem key={action.value} value={action.value}>
                                      {action.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <Badge>{entry.action_label}</Badge>
                            )}
                            <span className="text-xs text-slate-500 dark:text-slate-400">
                              {formatDateTime(entry.created_at)}
                            </span>
                            {entry.edited_at ? (
                              <span className="text-xs text-slate-500 dark:text-slate-400">
                                Edited {formatDateTime(entry.edited_at)}
                              </span>
                            ) : null}
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs text-slate-500 dark:text-slate-400">
                              {entry.created_by?.name || entry.created_by?.email || "Admin"}
                            </span>
                            {editingEntryId === entry.entry_id ? (
                              <>
                                <Button
                                  disabled={!editingEntryAction || savingEntryId === entry.entry_id}
                                  onClick={handleSaveEntryEdit}
                                  size="sm"
                                  type="button"
                                  variant="outline"
                                >
                                  {savingEntryId === entry.entry_id ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  ) : null}
                                  Save
                                </Button>
                                <Button onClick={handleCancelEditEntry} size="sm" type="button" variant="ghost">
                                  Cancel
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button onClick={() => handleStartEditEntry(entry)} size="sm" type="button" variant="ghost">
                                  Edit
                                </Button>
                                <Button
                                  disabled={deletingEntryId === entry.entry_id}
                                  onClick={() => handleDeleteEntry(entry)}
                                  size="sm"
                                  type="button"
                                  variant="ghost"
                                >
                                  {deletingEntryId === entry.entry_id ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  ) : null}
                                  Delete
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                        {entry.contact_value ? (
                          <p className="mt-3 text-sm text-slate-700 dark:text-slate-200">{entry.contact_value}</p>
                        ) : null}
                        {editingEntryId === entry.entry_id ? (
                          <Textarea
                            className="mt-3 min-h-[96px]"
                            onChange={(event) => setEditingEntryNote(event.target.value)}
                            value={editingEntryNote}
                          />
                        ) : entry.note ? (
                          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">{entry.note}</p>
                        ) : null}
                        {editingEntryId !== entry.entry_id && entry.edited_by?.name ? (
                          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                            Last edited by {entry.edited_by.name}
                          </p>
                        ) : null}
                      </div>
                    ))
                  ) : (
                    <p className="rounded-2xl border border-dashed border-slate-200 px-4 py-5 text-sm text-slate-500 dark:border-white/10 dark:text-slate-400">
                      No contact history has been logged yet.
                    </p>
                  )}
                </div>
              </section>

              <section className={`rounded-[28px] border p-5 ${appearance.panelClass}`}>
                <div className="border-b border-slate-200/80 pb-4 dark:border-white/10">
                  <h2 className="text-lg font-semibold text-slate-950 dark:text-white">Disposition history</h2>
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                    Shared source-status history for this community record across the Community layer and legacy admin edits.
                  </p>
                </div>

                <div className="mt-4 space-y-3">
                  {dispositionLoading ? (
                    <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white/85 px-4 py-4 text-sm text-slate-600 dark:border-white/10 dark:bg-white/[0.04] dark:text-slate-300">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading disposition history
                    </div>
                  ) : dispositionEntries.length ? (
                    dispositionEntries.map((entry) => (
                      <div
                        className="rounded-2xl border border-slate-200/80 bg-white/90 p-4 dark:border-white/10 dark:bg-white/[0.04]"
                        key={entry.event_id}
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge variant="outline">
                              {formatDispositionState(entry.previous_state)} to {formatDispositionState(entry.new_state)}
                            </Badge>
                            <span className="text-xs text-slate-500 dark:text-slate-400">
                              {formatDateTime(entry.changed_at)}
                            </span>
                          </div>
                          <span className="text-xs text-slate-500 dark:text-slate-400">
                            {entry.changed_by?.name || entry.changed_by?.email || "Admin"}
                          </span>
                        </div>
                        {entry.reason_label ? (
                          <p className="mt-3 text-sm text-slate-700 dark:text-slate-200">{entry.reason_label}</p>
                        ) : null}
                        {entry.note ? (
                          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">{entry.note}</p>
                        ) : null}
                      </div>
                    ))
                  ) : (
                    <p className="rounded-2xl border border-dashed border-slate-200 px-4 py-5 text-sm text-slate-500 dark:border-white/10 dark:text-slate-400">
                      No disposition history has been recorded yet.
                    </p>
                  )}

                  {dispositionError ? (
                    <p className="text-sm text-rose-600 dark:text-rose-300">{dispositionError}</p>
                  ) : null}
                </div>
              </section>
            </div>

            <div className="space-y-6">
              <section className={`rounded-[28px] border p-5 ${appearance.panelClass}`}>
                <h2 className="text-lg font-semibold text-slate-950 dark:text-white">Decision context</h2>
                <div className="mt-4 space-y-4">
                  <ContextBlock label="What this person needs now" value={item.what_this_person_needs_now} />
                  <ContextBlock label="Latest contact outcome" value={item.latest_contact_summary} />
                  <ContextBlock label="Next follow-up" value={item.next_follow_up_summary} />
                  <ContextBlock label="Last meaningful movement" value={item.last_meaningful_movement} />
                  <ContextBlock label="Next move" value={item.next_move} />
                  <ContextBlock label="Stakeholder notes" value={item.notes} />
                  {item.current_condition_note ? <ContextBlock label="Current condition" value={item.current_condition_note} /> : null}
                  {item.home_proof_signal ? <ContextBlock label="Home-visible proof" value={item.home_proof_signal} /> : null}
                  {item.weekly_rhythm_signal ? <ContextBlock label="Weekly rhythm signal" value={item.weekly_rhythm_signal} /> : null}
                </div>
              </section>

              <section className={`rounded-[28px] border p-5 ${appearance.panelClass}`}>
                <h2 className="text-lg font-semibold text-slate-950 dark:text-white">Set next move</h2>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                  Keep the working next step visible while you are focused on this person.
                </p>
                <div className="mt-4 flex gap-3">
                  <Input
                    className="flex-1"
                    onChange={(event) => setNextMoveDraft(event.target.value)}
                    value={nextMoveDraft}
                  />
                  <Button onClick={handleSaveNextMove}>Save</Button>
                </div>
              </section>

              <section className={`rounded-[28px] border p-5 ${appearance.panelClass}`}>
                <h2 className="text-lg font-semibold text-slate-950 dark:text-white">Follow-up plan</h2>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                  Keep the next outreach date, channel, and intent shared across devices.
                </p>
                <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
                  <Input
                    type="date"
                    value={followUpDateDraft}
                    onChange={(event) => setFollowUpDateDraft(event.target.value)}
                  />
                  <Select
                    value={followUpTypeDraft || "none"}
                    onValueChange={(value) => setFollowUpTypeDraft(value === "none" ? "" : value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose follow-up type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No type</SelectItem>
                      {STAKEHOLDER_FOLLOW_UP_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="mt-3">
                  <Select
                    value={urgencyOverrideDraft || "auto"}
                    onValueChange={(value) => setUrgencyOverrideDraft(value || "auto")}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Urgency override" />
                    </SelectTrigger>
                    <SelectContent>
                      {STAKEHOLDER_URGENCY_OVERRIDE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Textarea
                  className="mt-3 min-h-[88px]"
                  onChange={(event) => setFollowUpNoteDraft(event.target.value)}
                  placeholder="Why this follow-up matters, what to ask, or what to listen for."
                  value={followUpNoteDraft}
                />
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button disabled={savingFollowUp} onClick={handleSaveFollowUp}>
                    {savingFollowUp ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Save follow-up plan
                  </Button>
                  <Button
                    disabled={savingFollowUp}
                    onClick={handleClearFollowUp}
                    type="button"
                    variant="outline"
                  >
                    Clear follow-up
                  </Button>
                  <Button
                    disabled={savingFollowUp}
                    onClick={handleResetUrgency}
                    type="button"
                    variant="outline"
                  >
                    Reset urgency
                  </Button>
                </div>
              </section>

              {canManageSourceStatus ? (
                <section className={`rounded-[28px] border p-5 ${appearance.panelClass}`}>
                  <h2 className="text-lg font-semibold text-slate-950 dark:text-white">Status and disposition</h2>
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                    Update the real source record from here. Changes here affect the underlying admin system, not just this Community view.
                  </p>
                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                    <div>
                      <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                        Current source status
                      </p>
                      <Badge variant="outline">
                        {sourceStatusOptions.find((option) => option.value === currentSourceState)?.label || "Unknown"}
                      </Badge>
                    </div>
                    <div>
                      <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                        Change to
                      </p>
                      <Select value={targetStateDraft || "none"} onValueChange={(value) => setTargetStateDraft(value === "none" ? "" : value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose new status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No change</SelectItem>
                          {sourceStatusOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                    <div>
                      <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                        Reason
                      </p>
                      <Select value={dispositionReasonDraft || "none"} onValueChange={(value) => setDispositionReasonDraft(value === "none" ? "" : value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose reason" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No reason selected</SelectItem>
                          {STAKEHOLDER_DISPOSITION_REASON_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Textarea
                    className="mt-3 min-h-[88px]"
                    onChange={(event) => setDispositionNoteDraft(event.target.value)}
                    placeholder="Why are you moving or removing this person? This will be preserved for future analysis."
                    value={dispositionNoteDraft}
                  />
                  <div className="mt-3 flex items-center gap-3">
                    <Button
                      disabled={
                        savingStatusChange ||
                        !targetStateDraft ||
                        targetStateDraft === currentSourceState ||
                        (requiresDispositionReason(item, targetStateDraft) && !dispositionReasonDraft && !dispositionNoteDraft.trim())
                      }
                      onClick={handleApplyStatusChange}
                    >
                      {savingStatusChange ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                      Apply source status change
                    </Button>
                    {requiresDispositionReason(item, targetStateDraft) ? (
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        A reason is required for removal/disengagement.
                      </p>
                    ) : null}
                  </div>
                </section>
              ) : null}

              {item.display_detail_tags?.length ? (
                <section className={`rounded-[28px] border p-5 ${appearance.panelClass}`}>
                  <h2 className="text-lg font-semibold text-slate-950 dark:text-white">Context tags</h2>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {item.display_detail_tags.map((tag) => (
                      <Badge key={tag} variant="outline">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </section>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
